/**
 * API client base — points at bonusroll-api.
 *
 * In dev: reads NEXT_PUBLIC_API_URL (set in .env.local) or defaults to
 * http://localhost:8000. In prod: should be set via environment to
 * https://api.bonusroll.net.
 */

import { captureException } from "./analytics";

export const API_BASE =
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_URL) ||
  "http://localhost:8000";

/** Status codes we treat as "expected, don't bother PostHog":
 *    401  — anonymous user hitting an auth-gated endpoint
 *    404  — read-before-write (e.g. /api/state on a brand-new user)
 *  Everything else (4xx misuse + 5xx server bugs + network failures)
 *  reaches PostHog so we can spot regressions. */
const EXPECTED_STATUSES = new Set([401, 404]);

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch (networkErr) {
    // Connection refused / DNS / CORS preflight failure — never reaches
    // an HTTP status. Always reportable.
    captureException(networkErr, {
      api_path: path,
      method: init?.method ?? "GET",
      kind: "network_error",
    });
    throw networkErr;
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    const err = new Error(
      `API ${resp.status} ${path}: ${text || resp.statusText}`,
    );
    if (!EXPECTED_STATUSES.has(resp.status)) {
      captureException(err, {
        api_path: path,
        method: init?.method ?? "GET",
        status: resp.status,
        body_preview: text.slice(0, 200),
        kind: "http_error",
      });
    }
    throw err;
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/** GET that returns null on 404 instead of throwing. */
export async function apiGetOrNull<T>(path: string): Promise<T | null> {
  try {
    return await apiFetch<T>(path);
  } catch (err) {
    if (err instanceof Error && err.message.includes(" 404 ")) return null;
    throw err;
  }
}
